import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  IFFilter,
  IFText,
  IFButton,
  IFModal,
  IFTable,
  IFDialog,
  IFTooltipIconsLoading,
  IFsvg,
  IFCount,
} from 'Components'
import { AddVoucherForm } from 'Containers'
import Colors from 'Theme/Colors'
import Styles from './VouchersPage.module.css'
import { useTranslation } from 'react-i18next'
import RequestState from 'Enums/RequestState'
import VoucherSelectors from 'Stores/Voucher/Selectors'
import VoucherActions from 'Stores/Voucher/Actions'
import {
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  Tooltip,
  Popover,
} from '@mui/material'
import moment from 'moment'
import EditIcon from '@mui/icons-material/Edit'
import LoadingBar from 'react-top-loading-bar'
import AuthSelectors from 'Stores/Auth/Selectors'
import ReportIcon from '@mui/icons-material/Report'
import InfinityEnums from 'Enums/InfinityEnums'
import Scrollbars from 'react-custom-scrollbars'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import styled from 'styled-components'
import { useLocation } from 'react-router-dom'
import ClearIcon from '@mui/icons-material/Clear'
import { Actions, Subjects } from 'Enums/permissionKeys'

const IconDivIFToolTip = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 5px;
  transition: background-color 0.3s ease;
  cursor: pointer;
  &:hover {
    background-color: ${Colors.onHoverBackground};
  }
`
const VouchersPage = ({
  addVoucher,
  setVouchersFilter,
  clearVouchers,
  vouchersFilter,
  fetchVouchersListRequestState,
  fetchVouchers,
  paginationOffset,
  vouchersCount,
  fetchVouchersCountRequestState,
  vouchers,
  updateVoucher,
  updateVoucherRequestState,
  addVoucherRequestState,
  setUpdateVoucherRequestState,
  setAddVoucherRequestState,
  admin,
  clearVouchersFilter,
  editingAdminRole,
  deleteVoucherRequestState,
  deleteVoucher,
}) => {
  const { t } = useTranslation()
  const [isFirstLoaded, setIsFirstLoaded] = useState(true)
  const [table, setTable] = useState()
  const tablesRef = useRef(null)
  const voucherFilterRef = useRef(null)
  const [tablesHeight, setTablesHeight] = React.useState(0)
  const [registeredCountires, setRegisteredCountries] = React.useState(false)
  const [columnVisibility, setColumnVisibility] = useState({
    country: false,
  })
  const location = useLocation()
  useEffect(() => {
    if (location.state?.refresh) {
      clearVouchersFilter(admin)
      setIsFirstLoaded(true)
      voucherFilterRef?.current?.clearFilter()
    }
  }, [location.state?.refresh])
  useEffect(() => {
    return () => {
      clearVouchers()
      clearVouchersFilter(admin)
      setIsFirstLoaded(true)
      voucherFilterRef?.current?.clearFilter()
    }
  }, [])
  useEffect(() => {
    if (admin?.registeredCountries?.length > 1) {
      setRegisteredCountries(true)
      clearVouchersFilter(admin)
    }
  }, [admin])
  useEffect(() => {
    fetchVouchers(vouchersFilter, 0)
  }, [vouchersFilter])
  useEffect(() => {
    if (fetchVouchersListRequestState === RequestState.SUCCEEDED)
      setIsFirstLoaded(false)
  }, [fetchVouchersListRequestState])
  useEffect(() => {
    setColumnVisibility({
      country: registeredCountires,
    })
  }, [registeredCountires])
  const onSubmitFormHandler = (voucher) => {
    addVoucher(voucher)
  }
  const showDialogHandler = useRef()
  const showDialogHandlerDelete = useRef()

  const loadMoreData = () => {
    if (
      fetchVouchersListRequestState === RequestState.LOADING ||
      paginationOffset === null
    )
      return
    fetchVouchers(vouchersFilter, paginationOffset, failedRequest)
  }

  const fetchMoreOnBottomReached = useCallback(
    (event) => {
      if (event) {
        const { scrollHeight, scrollTop, clientHeight } = event
        if (scrollHeight - scrollTop - clientHeight < 400) {
          loadMoreData()
        }
      }
    },
    [loadMoreData],
  )
  const [failedRequest, setFailedRequest] = useState(false)
  useEffect(() => {
    if (
      fetchVouchersListRequestState === RequestState.ERROR_0_NETWORK ||
      fetchVouchersListRequestState === RequestState.ERROR_400_OCCURRED ||
      fetchVouchersListRequestState === RequestState.ERROR_401_OCCURRED ||
      fetchVouchersListRequestState === RequestState.ERROR_403_OCCURRED ||
      fetchVouchersListRequestState === RequestState.ERROR_409_OCCURRED ||
      fetchVouchersListRequestState === RequestState.ERROR_UNKNOWN_OCCURRED
    )
      setFailedRequest(true)
    if (fetchVouchersListRequestState === RequestState.SUCCEEDED)
      setFailedRequest(false)
  }, [fetchVouchersListRequestState])
  const handleRetry = () => {
    fetchVouchers(vouchersFilter, paginationOffset)
  }
  const [anchorEl, setAnchorEl] = useState(null)
  const [popoverContent, setPopoverContent] = useState('')
  const open = Boolean(anchorEl)

  const handlePopoverOpen = (event, row) => {
    const { name, code } = row.original
    const textOverFlow = event.currentTarget.querySelector(
      `.${Styles.TableText}`,
    )
    const isOverflowing = (element) =>
      element && element.scrollWidth > element.clientWidth

    if (isOverflowing(textOverFlow)) {
      setAnchorEl(event.currentTarget)
      setPopoverContent({ name, code })
    }
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const nameValue = useRef()
  const dateValue = useRef()
  const [dateVal, setDateVal] = useState()

  const maxRedeemCountValue = useRef()

  const vouchersTableColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        size: 150,
        muiEditTextFieldProps: ({ row }) => {
          const fullName = row.original.name || ''
          const expectedPrefix = row.original.country?.codeTwoLetters
            ? `${row.original.country.codeTwoLetters}-`
            : ''
          const hasPrefix =
            expectedPrefix && fullName.startsWith(expectedPrefix)
          const editablePart = hasPrefix
            ? fullName.slice(expectedPrefix.length)
            : fullName

          const [editableValue, setEditableValue] = useState(editablePart)
          useEffect(() => {
            setEditableValue(editablePart)
          }, [row.original.name, editablePart])

          useEffect(() => {
            nameValue.current = editableValue
          }, [editableValue])

          return {
            type: 'text',
            value: editableValue,
            onChange: (event) => {
              setEditableValue(event.target.value)
            },
            InputProps: hasPrefix
              ? {
                  startAdornment: (
                    <span style={{ marginRight: '2px', marginBottom: '3px' }}>
                      {expectedPrefix}
                    </span>
                  ),
                }
              : {},
            sx: {
              '& .MuiInputBase-root:hover::before': {
                borderBottom: `1px solid ${Colors.primary} !important`,
              },
              '& .MuiInputBase-root.Mui-focused::after': {
                borderBottom: `2px solid ${Colors.primary} !important`,
              },
            },
          }
        },
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersName')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <div
              onMouseEnter={(e) => handlePopoverOpen(e, row)}
              onMouseLeave={handlePopoverClose}
            >
              <IFText className={Styles.TableText}>{row.original.name}</IFText>
            </div>
          ) : failedRequest ? (
            <div className={Styles.IconDiv}>
              <ReportIcon
                width={28}
                height={28}
                style={{ color: Colors.red }}
              />
              <IFText>{t('IFTable.TryAgainText')}</IFText>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'code',
        enableEditing: false,
        size: 200,
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersCode')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <div
              onMouseEnter={(e) => handlePopoverOpen(e, row)}
              onMouseLeave={handlePopoverClose}
            >
              <IFText className={Styles.TableText}>{row.original.code}</IFText>
            </div>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'points',
        enableEditing: false,
        size: 100,
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersPoints')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <IFText>{row.original.points}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'RedeemPerUser',
        enableEditing: false,
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.RedeemPerUser')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <IFText>
              {row.original.maxRedeemCountPerUser
                ? row.original.maxRedeemCountPerUser
                : '\uFF3F'}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'maxRedeemCount',
        muiEditTextFieldProps: () => {
          return {
            type: 'number',
            inputProps: {
              min: 1,
            },
            value: maxRedeemCountValue?.current,
            onChange: (event) => {
              const value = event.target.value
              if (value < 1) {
                event.target.value = ''
              }
              maxRedeemCountValue.current = event.target.value
            },
            sx: {
              '& .MuiInputBase-root:hover::before': {
                borderBottom: `1px solid ${Colors.primary} !important`,
              },
              '& .MuiInputBase-root.Mui-focused::after': {
                borderBottom: `2px solid ${Colors.primary} !important`,
              },
            },
          }
        },
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersRedeemed')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <IFText>
              {row.original.redeemCount}/{row.original.maxRedeemCount}
            </IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'Validity',
        enableEditing: false,
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersValidity')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <IFText> {row.original.pointsExpireAfterDays}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'country',
        enableEditing: false,
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.Country')
          ),
        Cell: ({ row }) => {
          return row.index < vouchers.length ? (
            <IFText>{row.original.country.name}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'expiry',
        size: 180,
        muiEditTextFieldProps: () => {
          return {
            type: 'date',
            InputProps: {
              inputProps: {
                min: moment().format('YYYY-MM-DD'),
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      dateValue.current = ''
                      setDateVal('')
                    }}
                    edge="end"
                    className={Styles.ClearButton}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </InputAdornment>
              ),
            },
            sx: {
              '& .MuiInputBase-root:hover::before': {
                borderBottom: `1px solid ${Colors.primary} !important`,
              },
              '& .MuiInputBase-root.Mui-focused::after': {
                borderBottom: `2px solid ${Colors.primary} !important`,
              },
            },
            value: dateVal,
            onChange: (event) => {
              dateValue.current = event.target.value
              setDateVal(event.target.value)
            },
          }
        },
        header:
          fetchVouchersListRequestState === RequestState.LOADING &&
          isFirstLoaded ? (
            <Skeleton
              variant="text"
              width="6.875rem"
              animation="none"
              height={20}
            />
          ) : (
            t('VouchersPage.VouchersExpiry')
          ),
        Cell: ({ row }) => {
          let expiryDateMoment, expired, expiryText
          if (row.original.expiry) {
            expiryDateMoment = moment(row.original.expiry).format('YYYY-MM-DD')
            expired = moment().isSameOrAfter(moment(row.original.expiry))
              ? true
              : false
            expiryText = expired
              ? t('VouchersPage.ExpiredText')
              : t('VouchersPage.ExpiresOnText') + expiryDateMoment
          } else {
            expiryText = t('VouchersPage.NoExpiryDate')
          }
          return row.index < vouchers.length ? (
            <IFText>{expiryText}</IFText>
          ) : failedRequest ? (
            <></>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      vouchers,
      fetchVouchersListRequestState,
      isFirstLoaded,
      failedRequest,
      maxRedeemCountValue,
      nameValue,
      dateValue,
      dateVal,
    ],
  )

  const handleSaveVoucher = (table, row) => {
    const expectedPrefix = row.original.country?.codeTwoLetters
      ? `${row.original.country.codeTwoLetters}-`
      : ''
    const finalName =
      expectedPrefix && row.original.name.startsWith(expectedPrefix)
        ? `${expectedPrefix}${nameValue.current}`
        : nameValue.current

    if (expectedPrefix && nameValue.current.trim() === '') {
      table?.setEditingRow(null)
      return
    }
    let values = {
      name: finalName,
      maxRedeemCount: maxRedeemCountValue.current,
      expiry: dateValue.current,
    }
    if (
      values.name === '' ||
      values.maxRedeemCount === '' ||
      (values.name === row.original.name &&
        values.maxRedeemCount === row.original.maxRedeemCount &&
        ((values.expiry &&
          row.original.expiry &&
          values.expiry.slice(0, 10) === row.original.expiry.slice(0, 10)) ||
          (!values.expiry && !row.original.expiry)))
    ) {
      table?.setEditingRow(null)
    } else {
      setTable(table)
      updateVoucher(row.original.id, values, row.index, () => {})
    }
  }
  useEffect(() => {
    if (updateVoucherRequestState === RequestState.SUCCEEDED) {
      table?.setEditingRow(null)
    }
  }, [updateVoucherRequestState])

  useEffect(() => {
    if (addVoucherRequestState === RequestState.SUCCEEDED) {
      showDialogHandler.current.dismiss()
      setAddVoucherRequestState(RequestState.UNINITIALIZED)
    }
  }, [addVoucherRequestState])

  useEffect(() => {
    const handleResize = (entries) => {
      if (entries[0]) {
        const { height } = entries[0].contentRect
        setTablesHeight(height)
      }
    }

    const resizeObserver = new ResizeObserver(handleResize)

    if (tablesRef.current) {
      resizeObserver.observe(tablesRef.current)
    }

    return () => {
      if (tablesRef.current) {
        resizeObserver.unobserve(tablesRef.current)
      }
    }
  }, [])
  const handleEdit = ({ row, table }) => {
    nameValue.current = row.original.name
    dateValue.current = row.original.expiry
      ? moment(row.original.expiry).format('YYYY-MM-DD')
      : ''
    setDateVal(
      row.original.expiry
        ? moment(row.original.expiry).format('YYYY-MM-DD')
        : '',
    )

    maxRedeemCountValue.current = row.original.maxRedeemCount

    setUpdateVoucherRequestState(RequestState.UNINITIALIZED)
    table?.setEditingRow(row)
  }
  const [selectedRow, setSelectedRow] = useState()
  useEffect(() => {
    if (deleteVoucherRequestState === RequestState.SUCCEEDED) {
      showDialogHandlerDelete?.current?.dismiss()
      setSelectedRow()
    }
  }, [deleteVoucherRequestState])
  const handleDelete = (row) => {
    setSelectedRow(row.original.id)
    showDialogHandlerDelete.current.show()
  }
  const renderRowActionsCell = ({ table, row }) => {
    if (Object.keys(row.original).length > 0) {
      return row?.id === table.getState().editingRow?.id ? (
        <div className={Styles.RowActions}>
          {editingAdminRole?.can(Actions.DELETE, Subjects.VOUCHERS) &&
            row.original.redeemCount === 0 && (
              <>
                <Tooltip title={t('VouchersPage.Delete')}>
                  <IconButton
                    onClick={() => {
                      handleDelete(row)
                    }}
                  >
                    <div
                      className={Styles.DeleteIcon}
                      style={{ color: Colors.AdminListDeleteIcon }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </div>
                  </IconButton>
                </Tooltip>
                <IFDialog
                  ref={showDialogHandlerDelete}
                  open={false}
                  maxWidth={'xs'}
                  fullWidth={true}
                  title={t('VouchersPage.DeleteVoucher')}
                  bodyText={t('VouchersPage.DeleteVoucherText')}
                  buttonAcceptonClick={() => {
                    deleteVoucher(selectedRow)
                  }}
                  buttonCancelColor={Colors.UserPageCancel}
                  buttonAcceptColor={Colors.UserPageDeactivateButton}
                  buttonClassName={Styles.Button}
                  buttonAcceptText={t('VouchersPage.DeleteConfirm')}
                />
              </>
            )}
          <Tooltip title={t('VouchersPage.Save')}>
            <IconDivIFToolTip>
              <IFTooltipIconsLoading
                isLoading={
                  updateVoucherRequestState === RequestState.LOADING
                    ? true
                    : false
                }
                Icon={IFsvg.DoneBtn}
                FilledIcon={IFsvg.DoneBtn}
                size={24}
                animationDisabled={false}
                onClick={() => {
                  handleSaveVoucher(table, row)
                }}
              />
            </IconDivIFToolTip>
          </Tooltip>
          <Tooltip title={t('VouchersPage.Cancel')}>
            <IconButton
              onClick={() => {
                table?.setEditingRow(null)
              }}
            >
              <IFsvg.CloseBtn height={24} width={24} fill={Colors.primary} />
            </IconButton>
          </Tooltip>
        </div>
      ) : (
        <Box className={Styles.RowActions}>
          {editingAdminRole?.can(Actions.EDIT, Subjects.VOUCHERS) && (
            <Tooltip title={t('VouchersPage.Edit')}>
              <IconButton onClick={() => handleEdit({ row, table })}>
                <EditIcon sx={{ color: Colors.gray }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )
    }
    if (failedRequest) {
      return (
        <IFButton
          size="sm"
          color={Colors.red}
          text={t('IFTable.TryAgain')}
          onClick={() => {
            handleRetry()
          }}
        />
      )
    }
  }
  const [progress, setProgress] = useState(0)
  useEffect(() => {
    if (isFirstLoaded) {
      if (
        fetchVouchersListRequestState === RequestState.LOADING &&
        progress === 0
      )
        setProgress(progress + 10)

      if (fetchVouchersListRequestState === RequestState.SUCCEEDED)
        setProgress(100)

      if (
        fetchVouchersListRequestState !== RequestState.LOADING &&
        fetchVouchersListRequestState !== RequestState.UNINITIALIZED &&
        fetchVouchersListRequestState !== RequestState.SUCCEEDED
      )
        setProgress(100)
    }
  }, [fetchVouchersListRequestState])

  return (
    <div>
      <div className={Styles.WrapperContainer}>
        <div>
          <LoadingBar
            color={Colors.primary}
            progress={progress}
            onLoaderFinished={() => setProgress(0)}
          />
        </div>
        <div
          className={Styles.TopContainer}
          style={{
            backgroundColor: Colors.white,
            borderColor: Colors.UserPageBackgroundContainer,
          }}
        >
          <div className={Styles.UserDataContainer}>
            <div className={Styles.TitleContainer}>
              <div className={Styles.TitleDiv}>
                <IFText
                  className={Styles.TitleText}
                  loadSkeleton={
                    fetchVouchersListRequestState === RequestState.LOADING &&
                    isFirstLoaded
                  }
                  skeletonWidth={'5rem'}
                >
                  {t('VouchersPage.VouchersTitle')}
                </IFText>
                <IFCount
                  count={vouchersCount}
                  isLoading={
                    (fetchVouchersListRequestState === RequestState.LOADING ||
                      fetchVouchersCountRequestState ===
                        RequestState.LOADING) &&
                    paginationOffset === 0
                  }
                />
              </div>
              <div>
                {editingAdminRole?.can(Actions.CREATE, Subjects.VOUCHERS) && (
                  <IFButton
                    size="sm"
                    color={Colors.primary}
                    isFill={false}
                    isLoading={false}
                    text={t('VouchersPage.Add')}
                    className={Styles.CreateButton}
                    onClick={() => showDialogHandler.current.show()}
                    loadSkeleton={
                      fetchVouchersListRequestState === RequestState.LOADING &&
                      isFirstLoaded
                    }
                  />
                )}
              </div>
            </div>

            <div className={Styles.Filter}>
              <IFFilter
                ref={voucherFilterRef}
                onFilterChange={(newFilter) => {
                  clearVouchers()
                  setVouchersFilter(newFilter)
                }}
                isLoading={
                  fetchVouchersListRequestState === RequestState.LOADING &&
                  isFirstLoaded
                }
                filters={vouchersFilter}
                textFieldPlaceholder={t(
                  'VouchersPage.VouchersFilterPlaceholder',
                )}
              />
            </div>
            <Scrollbars autoHide className={Styles.ScrollParent}>
              <div className={Styles.Tables} ref={tablesRef}>
                <IFTable
                  columns={vouchersTableColumns}
                  data={vouchers}
                  fetchMoreOnBottomReached={fetchMoreOnBottomReached}
                  paginationOffset={paginationOffset}
                  enableEditing={true}
                  isSaving={
                    updateVoucherRequestState === RequestState.LOADING
                      ? true
                      : false
                  }
                  filters={vouchersFilter}
                  showSkeleton={
                    (fetchVouchersListRequestState === RequestState.LOADING &&
                      paginationOffset === 0) ||
                    (fetchVouchersListRequestState !== RequestState.SUCCEEDED &&
                      !paginationOffset)
                  }
                  tableMaxHeight={tablesHeight - 16}
                  tableSetHeight={'100%'}
                  hideColumnQuery={columnVisibility}
                  tableRequestState={fetchVouchersListRequestState}
                  renderRowActionsCell={renderRowActionsCell}
                />

                <Popover
                  id="mouse-over-popover"
                  className={Styles.Popover}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handlePopoverClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  disableRestoreFocus
                >
                  <Box
                    className={Styles.ParentBox}
                    sx={{
                      p: 2,
                    }}
                  >
                    <Box className={Styles.ChildBox}>
                      <IFText style={{ color: Colors.DetailPanel }}>
                        <strong>{t('VouchersPage.VouchersName')}</strong>
                      </IFText>
                      <IFText
                        className={Styles.PopoverText}
                        style={{
                          color: Colors.text,
                        }}
                      >
                        {popoverContent.name}
                      </IFText>
                    </Box>

                    <Box className={Styles.SecondChildBox}>
                      <IFText style={{ color: Colors.DetailPanel }}>
                        <strong>{t('VouchersPage.VouchersCode')}</strong>
                      </IFText>
                      <IFText
                        className={Styles.PopoverText}
                        style={{
                          color: Colors.text,
                        }}
                      >
                        {popoverContent.code}
                      </IFText>
                    </Box>
                  </Box>
                </Popover>
              </div>
            </Scrollbars>
          </div>
        </div>
      </div>
      <IFModal
        title={t('VouchersPage.AddNewVoucher')}
        open={false}
        ref={showDialogHandler}
        style={{ padding: '16px 0px 16px 16px' }}
        exitIconStyle={{ marginRight: '16px' }}
      >
        <AddVoucherForm
          onSubmitForm={(values) => onSubmitFormHandler(values)}
        />
      </IFModal>
    </div>
  )
}
function mapDispatchToProps(dispatch) {
  return {
    setVouchersFilter: (newFilter) =>
      dispatch(VoucherActions.setVouchersFilter(newFilter)),
    clearVouchers: () => dispatch(VoucherActions.clearVouchers()),
    clearVouchersFilter: (admin) =>
      dispatch(VoucherActions.clearVouchersFilter(admin)),
    fetchVouchers: (filter, offset, shouldShowError) =>
      dispatch(VoucherActions.fetchVouchers(filter, offset, shouldShowError)),
    addVoucher: (voucher) => dispatch(VoucherActions.addVoucher(voucher)),
    updateVoucher: (voucherId, newVoucher, index, onResponse) =>
      dispatch(
        VoucherActions.updateVoucher(voucherId, newVoucher, index, onResponse),
      ),
    setUpdateVoucherRequestState: (newState) =>
      dispatch(VoucherActions.setUpdateVoucherRequestState(newState)),
    setAddVoucherRequestState: (newState) =>
      dispatch(VoucherActions.setAddVoucherRequestState(newState)),
    deleteVoucher: (voucherId) =>
      dispatch(VoucherActions.deleteVoucher(voucherId)),
  }
}
const mapStateToProps = (state) => ({
  vouchersFilter: VoucherSelectors.getVouchersFilter(state),
  vouchers: VoucherSelectors.getVouchersList(state),
  fetchVouchersListRequestState:
    VoucherSelectors.getFetchVouchersListRequestState(state),
  paginationOffset: VoucherSelectors.getPaginationOffset(state),
  vouchersCount: VoucherSelectors.getVouchersCount(state),
  fetchVouchersCountRequestState:
    VoucherSelectors.getVouchersCountRequestState(state),
  updateVoucherRequestState:
    VoucherSelectors.getUpdateVoucherRequestState(state),
  addVoucherRequestState: VoucherSelectors.getAddVoucherRequestState(state),
  admin: AuthSelectors.getUser(state),
  editingAdminRole: AuthSelectors.getEditingAdminRole(state),
  deleteVoucherRequestState:
    VoucherSelectors.getDeleteVoucherRequestState(state),
})
export default connect(mapStateToProps, mapDispatchToProps)(VouchersPage)
